import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './PomodoroModal.module.css'

const DEFAULT_FOCUS_MIN = 25
const DEFAULT_BREAK_MIN = 5
const MIN_MINUTES = 1
const MAX_MINUTES = 180

export type PomodoroModalProps = {
  isOpen: boolean
  taskTitle: string
  onClose: () => void
}

type Phase = 'setup' | 'work' | 'break' | 'done'

const clampMinutes = (n: number) =>
  Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, Math.floor(Number.isFinite(n) ? n : MIN_MINUTES)))

const formatMmSs = (totalSeconds: number) => {
  const s = Math.max(0, totalSeconds)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

const PomodoroModal = ({ isOpen, taskTitle, onClose }: PomodoroModalProps) => {
  const [focusMinutes, setFocusMinutes] = useState(DEFAULT_FOCUS_MIN)
  const [breakMinutes, setBreakMinutes] = useState(DEFAULT_BREAK_MIN)
  const [phase, setPhase] = useState<Phase>('setup')
  const [secondsLeft, setSecondsLeft] = useState(DEFAULT_FOCUS_MIN * 60)
  const [breakSecondsSnap, setBreakSecondsSnap] = useState(DEFAULT_BREAK_MIN * 60)
  const [isRunning, setIsRunning] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setFocusMinutes(DEFAULT_FOCUS_MIN)
    setBreakMinutes(DEFAULT_BREAK_MIN)
    setPhase('setup')
    setSecondsLeft(DEFAULT_FOCUS_MIN * 60)
    setBreakSecondsSnap(DEFAULT_BREAK_MIN * 60)
    setIsRunning(false)
  }, [isOpen, taskTitle])

  useEffect(() => {
    if (!isOpen || !isRunning) return
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => window.clearInterval(id)
  }, [isOpen, isRunning])

  useEffect(() => {
    if (!isOpen || !isRunning || secondsLeft > 0) return
    if (phase === 'work') {
      setPhase('break')
      setSecondsLeft(breakSecondsSnap)
      return
    }
    if (phase === 'break') {
      setPhase('done')
      setIsRunning(false)
    }
  }, [isOpen, isRunning, secondsLeft, phase, breakSecondsSnap])

  const phaseLabel = useMemo(() => {
    if (phase === 'work') return 'Focus'
    if (phase === 'break') return 'Break'
    if (phase === 'done') return 'Session complete'
    return 'Ready'
  }, [phase])

  const displaySeconds = useMemo(() => {
    if (phase === 'setup') return clampMinutes(focusMinutes) * 60
    return secondsLeft
  }, [phase, focusMinutes, secondsLeft])

  const onFocusMinChange = (raw: string) => {
    const n = Number.parseInt(raw, 10)
    const v = clampMinutes(Number.isNaN(n) ? MIN_MINUTES : n)
    setFocusMinutes(v)
    if (phase === 'setup') setSecondsLeft(v * 60)
  }

  const onBreakMinChange = (raw: string) => {
    const n = Number.parseInt(raw, 10)
    setBreakMinutes(clampMinutes(Number.isNaN(n) ? MIN_MINUTES : n))
  }

  const startOrResume = useCallback(() => {
    const f = clampMinutes(focusMinutes)
    const b = clampMinutes(breakMinutes)
    setBreakSecondsSnap(b * 60)
    if (phase === 'setup' || phase === 'done') {
      setFocusMinutes(f)
      setBreakMinutes(b)
      setSecondsLeft(f * 60)
      setPhase('work')
      setIsRunning(true)
      return
    }
    if (phase === 'work' || phase === 'break') {
      setIsRunning(true)
    }
  }, [phase, focusMinutes, breakMinutes])

  const pause = useCallback(() => {
    setIsRunning(false)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const inputsLocked = isRunning || (phase !== 'setup' && phase !== 'done')
  const showPrimaryStart =
    phase === 'setup' || phase === 'done' || (!isRunning && (phase === 'work' || phase === 'break'))

  const modal = (
    <div
      className={styles.backdrop}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pomodoro-task-title"
      >
        <div className={styles.header}>
          <div className={styles.titleBlock}>
            <p className={styles.kicker}>Pomodoro</p>
            <h2 id="pomodoro-task-title" className={styles.taskTitle}>
              {taskTitle}
            </h2>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close timer">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {phase === 'done' ? (
          <p className={styles.doneHint}>Nice work. Start another round when you are ready.</p>
        ) : null}

        <p className={styles.phaseLabel}>{phaseLabel}</p>
        <div className={styles.timer} aria-live="polite">
          {formatMmSs(displaySeconds)}
        </div>

        <div className={styles.fields}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Focus (minutes)</span>
            <input
              className={styles.fieldInput}
              type="number"
              min={MIN_MINUTES}
              max={MAX_MINUTES}
              value={focusMinutes}
              disabled={inputsLocked}
              onChange={(e) => onFocusMinChange(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Break (minutes)</span>
            <input
              className={styles.fieldInput}
              type="number"
              min={MIN_MINUTES}
              max={MAX_MINUTES}
              value={breakMinutes}
              disabled={inputsLocked}
              onChange={(e) => onBreakMinChange(e.target.value)}
            />
          </label>
        </div>

        <div className={styles.actions}>
          {showPrimaryStart ? (
            <button type="button" className={styles.btnPrimary} onClick={startOrResume}>
              {phase === 'setup' ? 'Start' : phase === 'done' ? 'Start again' : 'Resume'}
            </button>
          ) : (
            <button type="button" className={styles.btnSecondary} onClick={pause}>
              Pause
            </button>
          )}
          <button type="button" className={styles.btnSecondary} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

export default PomodoroModal
